import { supabase } from '../lib/supabase';

/**
 * Send an email using the Supabase Edge Function
 * @param to Recipient email address
 * @param subject Email subject
 * @param text Plain text content (optional if html is provided)
 * @param html HTML content (optional if text is provided)
 * @param from Sender email address (optional, defaults to SMTP_USER)
 * @returns Promise with the result of the email sending operation
 */
export async function sendEmail({
  to,
  subject,
  text,
  html,
  from
}: {
  to: string;
  subject: string;
  text?: string;
  html?: string;
  from?: string;
}): Promise<{ success: boolean; messageId?: string; error?: string }> {
  try {
    // Validate required fields
    if (!to || !subject || (!text && !html)) {
      throw new Error('Missing required fields: to, subject, and either text or html');
    }

    // Get the Supabase URL from environment variables
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('Supabase URL not configured');
    }

    // Call the Supabase Edge Function
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: {
        to,
        subject,
        text,
        html,
        from
      }
    });

    if (error) {
      console.error('Error calling send-email function:', error);
      return { success: false, error: error.message };
    }

    return data;
  } catch (error: any) {
    console.error('Error in sendEmail:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Send a security alert email
 * @param to Recipient email address
 * @param ipAddress The suspicious IP address
 * @param userAgent The browser/device information
 * @returns Promise with the result of the email sending operation
 */
export async function sendSecurityAlertEmail(
  to: string,
  ipAddress: string,
  userAgent: string
): Promise<{ success: boolean; messageId?: string; error?: string }> {
  const subject = 'THRIVE - Security Alert: Suspicious Login Detected';
  
  const html = `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e2e8f0; border-radius: 5px;">
      <div style="background-color: #fef3c7; padding: 15px; border-radius: 5px; margin-bottom: 20px; display: flex; align-items: center;">
        <div style="margin-right: 10px;">⚠️</div>
        <h2 style="margin: 0; color: #92400e; font-size: 18px;">Security Alert: Suspicious Login Detected</h2>
      </div>
      
      <p style="color: #4b5563; line-height: 1.5;">
        We detected a login to your THRIVE account from an unfamiliar location.
      </p>
      
      <div style="background-color: #f9fafb; padding: 15px; border-radius: 5px; margin: 20px 0;">
        <p style="margin: 0 0 10px 0; font-weight: bold;">Login Details:</p>
        <ul style="margin: 0; padding-left: 20px;">
          <li><strong>IP Address:</strong> ${ipAddress}</li>
          <li><strong>Device/Browser:</strong> ${userAgent}</li>
          <li><strong>Time:</strong> ${new Date().toLocaleString()}</li>
        </ul>
      </div>
      
      <p style="color: #4b5563; line-height: 1.5;">
        If this was you, you can ignore this message. If you don't recognize this activity, please:
      </p>
      
      <ul style="color: #4b5563; line-height: 1.5;">
        <li>Change your password immediately</li>
        <li>Contact your system administrator</li>
        <li>Review your recent account activity</li>
      </ul>
      
      <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #6b7280;">
        <p>This is an automated message from THRIVE. Please do not reply to this email.</p>
      </div>
    </div>
  `;
  
  return sendEmail({
    to,
    subject,
    html
  });
}